import { Empty, Input, theme } from 'antd'
import { useMemo, useState } from 'react'
import { HiSearch } from 'react-icons/hi'
import { IoChevronForward } from 'react-icons/io5'

import { useMenuNavigation } from '@/Core/Hooks/useMenuNavigation'
import { classNames } from '@/Ship/Helpers/classNames'

import { FALLBACK_GROUP, LIST_GROUPS } from '../Contracts/ListGroups'

import type { MenuItems } from '@/Core/Contracts/UI/Menu'
import type { CSSProperties, FC, ReactNode } from 'react'

import styles from './SettingsList.module.css'

interface SettingsListProps {
    menuItemsBase: MenuItems
}

interface Book {
    key: string
    label: string
    description: string
    icon: ReactNode
}

interface Group {
    key: string
    title: string
    books: Book[]
}

/**
 * Сторінка «Довідники» — книги, розкладені за групами.
 *
 * Два десятки карток одним алфавітним списком читаються лише за підписами:
 * щоб знайти потрібний довідник, треба пробігти очима половину екрана. Групи
 * повторюють бокове меню, тож довідник лежить там, де з ним працюють. Склад
 * груп і описи — `Contracts/ListGroups`.
 *
 * Пошук іде і за назвою, і за описом: слово з опису знаходить довідник, хоча в
 * назві такого слова немає. Групи без збігів ховаються разом із заголовком.
 *
 * ⚠️ Класи `itemCard` та `iconContainer` зберігають ім'я навмисно: за ними e2e
 * перевіряють, що в кожної книги своя іконка (`c9`, `c10`).
 */
export const SettingsList: FC<SettingsListProps> = ({ menuItemsBase }) => {
    const { token } = theme.useToken()
    const [search, setSearch] = useState('')

    // Назви, іконки й перехід — ті самі, що в меню: ядро бере їх із маршрутів
    const { menuItems, handleMenuClick } = useMenuNavigation(menuItemsBase)

    const groups = useMemo<Group[]>(() => {
        const byKey = new Map<string, { label: string; icon: ReactNode }>()

        for (const item of menuItems) {
            if (typeof item?.key !== 'string') continue

            byKey.set(item.key, { label: String(item.label ?? item.key), icon: item.icon })
        }

        const placed = new Set<string>()

        const result: Group[] = LIST_GROUPS.map((group) => ({
            key: group.key,
            title: group.title,
            books: group.items.flatMap(([key, description]) => {
                const item = byKey.get(key)

                // Довідник, до якого в користувача немає доступу, у меню не
                // потрапляє — і групі його показувати нема чого
                if (!item) return []

                placed.add(key)

                return [{ key, label: item.label, description, icon: item.icon }]
            }),
        }))

        // Нерозкладені не губимо: вони йдуть окремою групою в кінці
        const rest = [...byKey.entries()]
            .filter(([key]) => !placed.has(key))
            .map(([key, item]) => ({ key, label: item.label, description: '', icon: item.icon }))

        if (rest.length > 0) result.push({ ...FALLBACK_GROUP, books: rest })

        return result
    }, [menuItems])

    const total = groups.reduce((sum, group) => sum + group.books.length, 0)

    const visible = useMemo<Group[]>(() => {
        const query = search.trim().toLowerCase()

        if (!query) return groups.filter((group) => group.books.length > 0)

        return groups
            .map((group) => ({
                ...group,
                books: group.books.filter(
                    (book) =>
                        book.label.toLowerCase().includes(query) || book.description.toLowerCase().includes(query)
                ),
            }))
            .filter((group) => group.books.length > 0)
    }, [groups, search])

    const palette = {
        '--lists-card-bg': token.colorBgContainer,
        '--lists-border': token.colorBorderSecondary,
        '--lists-border-hover': token.colorPrimaryBorder,
        '--lists-shadow-hover': token.colorPrimaryBg,
        '--lists-icon-bg': token.colorPrimaryBg,
        '--lists-icon-color': token.colorPrimary,
        '--lists-title': token.colorText,
        '--lists-description': token.colorTextSecondary,
        '--lists-group': token.colorTextTertiary,
        '--lists-count-bg': token.colorFillSecondary,
        '--lists-chevron': token.colorTextQuaternary,
    } as CSSProperties

    return (
        <div className={classNames(styles['container'])} style={palette}>
            <div className={classNames(styles['header'])}>
                <div>
                    <div className={classNames(styles['headerTitle'])}>Довідники</div>
                    <div className={classNames(styles['headerCount'])}>Усього: {total}</div>
                </div>

                <Input
                    allowClear
                    size="large"
                    placeholder="Пошук..."
                    prefix={<HiSearch className={classNames(styles['searchIcon'])} />}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className={classNames(styles['search'])}
                />
            </div>

            {visible.length === 0 ? (
                <div className={classNames(styles['empty'])}>
                    <Empty description={search ? 'Нічого не знайдено' : 'Немає доступних довідників'} />
                </div>
            ) : (
                visible.map((group) => (
                    <section key={group.key} className={classNames(styles['group'])} aria-label={group.title}>
                        <div className={classNames(styles['groupHeader'])}>
                            <span className={classNames(styles['groupTitle'])}>{group.title}</span>
                            <span className={classNames(styles['groupCount'])}>{group.books.length}</span>
                        </div>

                        <div className={classNames(styles['grid'])}>
                            {group.books.map((book) => (
                                <button
                                    key={book.key}
                                    type="button"
                                    className={classNames(styles['itemCard'])}
                                    onClick={() => handleMenuClick({ key: book.key })}
                                >
                                    <span className={classNames(styles['iconContainer'])}>{book.icon}</span>

                                    <span className={classNames(styles['text'])}>
                                        <span className={classNames(styles['itemLabel'])}>{book.label}</span>
                                        {book.description ? (
                                            <span className={classNames(styles['itemDescription'])} title={book.description}>
                                                {book.description}
                                            </span>
                                        ) : null}
                                    </span>

                                    <IoChevronForward className={classNames(styles['chevron'])} />
                                </button>
                            ))}
                        </div>
                    </section>
                ))
            )}
        </div>
    )
}
